/**
 * Delivery Service
 *
 * API client for the viewer's own delivery history (My Deliveries).
 * Calls the /delivery-audit/my-deliveries endpoint which returns
 * deliveries filtered by the authenticated viewer's email (from JWT).
 */

import apiClient from './api';

/**
 * Get the authenticated viewer's delivery history with optional filters.
 *
 * @param {object} params
 * @param {number} [params.page=1]       Current page number
 * @param {number} [params.pageSize=25]  Items per page
 * @param {string} [params.search='']    Free-text report name filter
 * @param {string} [params.dateFrom='']  ISO date lower bound
 * @param {string} [params.dateTo='']    ISO date upper bound
 * @param {string} [params.teamId='']    Filter by team (from active team selector)
 * @returns {Promise<{deliveries: Array, pagination: object}>}
 */
export const getMyDeliveries = async (params = {}) => {
  const { page = 1, pageSize = 25, search = '', dateFrom = '', dateTo = '', teamId = '' } = params;

  const queryParams = { page, page_size: pageSize };
  if (search) queryParams.search = search;
  if (dateFrom) queryParams.date_from = dateFrom;
  if (dateTo) queryParams.date_to = dateTo;
  if (teamId) queryParams.team_id = teamId;

  const response = await apiClient.get('/delivery-audit/my-deliveries', {
    params: queryParams
  });

  const apiData = response.data?.data || response.data;

  const deliveries = (apiData?.deliveries || []).map(d => ({
    deliveryId: d.delivery_id,
    reportName: d.report_name,
    timestamp: d.timestamp,
    deliveryStatus: d.delivery_status || 'sent',
    signedUrl: d.signed_url,
    clickCount: d.click_count || 0,
    isResend: d.is_resend || false,
    urlExpired: d.url_expired
  }));

  return {
    deliveries,
    pagination: {
      page: apiData?.pagination?.page || page,
      pageSize: apiData?.pagination?.page_size || pageSize,
      total: apiData?.pagination?.total || 0,
      totalPages: apiData?.pagination?.total_pages || 1
    }
  };
};
